import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { Search, Printer, AlertTriangle, CheckCircle, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { hiddenClientFields } from "@/lib/client-privacy";
import { getPrivacySession, privacySignature } from "@/lib/privacy-session";
import { formatPhone } from "@/lib/utils";
import ReportTableScroll from "@/components/ReportTableScroll";
import "./DistributionChecklist.css";

const TH = "text-left font-semibold uppercase tracking-wider text-xs";
const muted = { color: "var(--fb-text-muted)" };
const border = { borderColor: "var(--fb-border)" };
const EMPTY_ROWS = [];

export function distributionScope(user, year) {
  const pages = user?.page_permissions ?? null;
  return JSON.stringify([year, user?.id || user?.email || "", user?.role || "", hiddenClientFields(user).slice().sort(),
    pages == null ? null : (Array.isArray(pages) ? pages.slice().sort() : pages)]);
}

export function parseBagCount(text) {
  const t = (text ?? "").trim();
  if (t === "") return { value: null };
  if (!/^\d+$/.test(t) || !Number.isSafeInteger(Number(t))) return { error: "Enter a whole number, 0 or higher (up to 9,007,199,254,740,991)." };
  return { value: parseInt(t, 10) };
}

export function matchesDistributionSearch(row, query, nameHidden) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const hay = [row.household_id, nameHidden ? "" : row.contact_name];
  return hay.some((v) => v && String(v).toLowerCase().includes(q));
}

export function savedRowsSorted(rows, nameHidden) {
  const cmp = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" });
  return [...rows]
    .sort((a, b) => (nameHidden ? 0 : cmp(a.contact_name, b.contact_name)) || cmp(a.household_id, b.household_id));
}

function checkedData(data, year) {
  if (data?.year !== year || !Array.isArray(data.rows) || typeof data.editable !== "boolean" ||
      data.rows.some((row) => !row.household_record_id || !row.household_id ||
        !Number.isSafeInteger(row.bag_count_version) || row.bag_count_version < 0 ||
        (row.bag_count !== null && (!Number.isSafeInteger(row.bag_count) || row.bag_count < 0)))) {
    throw new Error("The complete checklist could not be verified. Retry loading before printing.");
  }
  return data;
}

const baseText = (r) => (r.bag_count === null || r.bag_count === undefined ? "" : String(r.bag_count));
const tierText = (v) => (v === null || v === undefined || v === "" ? "—" : `$${v}`);

export default function DistributionChecklist({ year, user, guardRef }) {
  const scope = distributionScope(user, year);
  const hidden = hiddenClientFields(user);
  const nameHidden = hidden.includes("names");
  const canWriteRole = ["admin", "staff"].includes(user?.role);
  const scopeRef = useRef(scope); scopeRef.current = scope;
  const alive = useRef(true);
  const seq = useRef(0);
  const [data, setData] = useState(null); // {scope, year, editable, rows}
  const [loadError, setLoadError] = useState("");
  const [drafts, setDrafts] = useState({});
  const [status, setStatus] = useState({}); // id -> {saving,error,conflict,saved,rebased}
  const [query, setQuery] = useState("");
  const [warning, setWarning] = useState("");
  const [printState, setPrintState] = useState(null); // {loading,error,rows}
  const printStateRef = useRef(printState); printStateRef.current = printState;
  const draftsRef = useRef(drafts); draftsRef.current = drafts;
  const statusRef = useRef(status); statusRef.current = status;
  const dataRef = useRef(data); dataRef.current = data;
  const printSeq = useRef(0);
  const sigRef = useRef(privacySignature(getPrivacySession()));

  const valid = data && data.scope === scope;
  const rows = valid ? data.rows : EMPTY_ROWS;
  const editable = !!(valid && data.editable && canWriteRole);

  const dirtyIds = () => Object.keys(draftsRef.current).filter((id) => {
    const r = (dataRef.current?.rows || []).find((x) => x.household_record_id === id);
    return r && draftsRef.current[id] !== baseText(r);
  });
  const anyPending = () => Object.values(statusRef.current).some((s) => s?.saving || s?.reloading);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    const loadScope = scopeRef.current;
    setData(null);
    setLoadError("");
    try {
      const { data: response } = await api.get(`/sharing-tree/distribution?year=${year}`);
      if (!alive.current || mine !== seq.current || scopeRef.current !== loadScope) return;
      const d = checkedData(response, year);
      setData({ scope: loadScope, editable: d.editable, rows: savedRowsSorted(d.rows, nameHidden) });
    } catch (e) {
      if (alive.current && mine === seq.current && scopeRef.current === loadScope) setLoadError(formatApiError(e));
    }
  }, [year, nameHidden]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  useEffect(() => {
    setData(null); setDrafts({}); setStatus({}); setPrintState(null); setWarning("");
    sigRef.current = privacySignature(getPrivacySession());
    load();
    return () => { seq.current += 1; };
  }, [scope, load]);

  if (guardRef) {
    guardRef.current = {
      isPending: () => anyPending(),
      isDirty: () => anyPending() || dirtyIds().length > 0,
      check: () => !(anyPending() || dirtyIds().length > 0),
    };
  }
  useEffect(() => () => { if (guardRef) guardRef.current = { isPending: () => false, isDirty: () => false, check: () => true }; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onUnload = (e) => { if (anyPending() || dirtyIds().length) { e.preventDefault(); e.returnValue = ""; } };
    const onPrivacy = () => {
      printSeq.current += 1;
      seq.current += 1;
      document.body.classList.remove("fb-dist-print-ready");
      setPrintState(null); setData(null); setDrafts({}); setStatus({});
    };
    window.addEventListener("beforeunload", onUnload);
    window.addEventListener("fb-client-privacy-changed", onPrivacy);
    return () => { window.removeEventListener("beforeunload", onUnload); window.removeEventListener("fb-client-privacy-changed", onPrivacy); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!printState) return undefined;
    document.body.classList.add("fb-dist-printing");
    return () => { document.body.classList.remove("fb-dist-printing", "fb-dist-print-ready"); };
  }, [!!printState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!printState) return undefined;
    // A print preview is sensitive data, not a detached permanent copy. Periodic
    // fresh reads go through the authoritative account/privacy interceptor.
    const refresh = async () => {
      if (printStateRef.current?.loading) return;
      const mScope = scopeRef.current;
      const mSequence = printSeq.current;
      try {
        const { data: response } = await api.get(`/sharing-tree/distribution?year=${year}`);
        if (!alive.current || scopeRef.current !== mScope || printSeq.current !== mSequence) return;
        const d = checkedData(response, year);
        setPrintState((p) => p && !p.loading ? { ...p, rows: savedRowsSorted(d.rows, nameHidden), error: "" } : p);
      } catch (e) {
        if (!alive.current || scopeRef.current !== mScope || printSeq.current !== mSequence) return;
        document.body.classList.remove("fb-dist-print-ready");
        setData(null);
        setLoadError(formatApiError(e));
        setPrintState((p) => p && { ...p, rows: [], error: formatApiError(e) });
      }
    };
    const interval = window.setInterval(refresh, 15000);
    const afterPrint = () => document.body.classList.remove("fb-dist-print-ready");
    window.addEventListener("afterprint", afterPrint);
    return () => { window.clearInterval(interval); window.removeEventListener("afterprint", afterPrint); };
  }, [!!printState]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRowStatus = (id, patch) => setStatus((s) => ({ ...s, [id]: patch === null ? undefined : { ...(s[id] || {}), ...patch } }));
  const patchRow = (id, patch) => setData((d) => (d ? { ...d, rows: d.rows.map((r) => (r.household_record_id === id ? { ...r, ...patch } : r)) } : d));

  function edit(id, text) {
    setWarning("");
    setDrafts((d) => ({ ...d, [id]: text }));
    setRowStatus(id, { saved: false, error: "" });
  }

  async function save(row) {
    const id = row.household_record_id;
    const st = statusRef.current[id] || {};
    if (st.saving || st.reloading || st.conflict || !editable ||
        draftsRef.current[id] === undefined || draftsRef.current[id] === baseText(row)) return;
    const parsed = parseBagCount(draftsRef.current[id]);
    if (parsed.error) { setRowStatus(id, { error: parsed.error }); return; }
    const mScope = scopeRef.current;
    setRowStatus(id, { saving: true, error: "", saved: false });
    try {
      const { data: res } = await api.patch(`/sharing-tree/households/${id}/bag-count`,
        { bag_count: parsed.value, expected_version: row.bag_count_version });
      if (!alive.current || scopeRef.current !== mScope) return;
      if (res.bag_count !== parsed.value || !Number.isSafeInteger(res.bag_count_version) ||
          res.bag_count_version !== row.bag_count_version + 1) throw new Error("The saved count could not be verified. Reload the current count.");
      patchRow(id, { bag_count: res.bag_count, bag_count_version: res.bag_count_version });
      setDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      setRowStatus(id, { saving: false, saved: true, error: "", rebased: false });
      setWarning("");
    } catch (e) {
      if (!alive.current || scopeRef.current !== mScope) return;
      if (e?.response?.status === 409) setRowStatus(id, { saving: false, conflict: true, error: "" });
      else setRowStatus(id, { saving: false, error: `Not saved: ${formatApiError(e)}. Your entry is kept.` });
    }
  }

  async function reloadRow(row) {
    const id = row.household_record_id;
    const mScope = scopeRef.current;
    setRowStatus(id, { reloading: true, error: "" });
    try {
      const { data: response } = await api.get(`/sharing-tree/distribution?year=${year}`);
      if (!alive.current || scopeRef.current !== mScope) return;
      const d = checkedData(response, year);
      const fresh = (d.rows || []).find((r) => r.household_record_id === id);
      if (!fresh) throw new Error("Household no longer on the distribution list.");
      patchRow(id, { bag_count: fresh.bag_count, bag_count_version: fresh.bag_count_version });
      setData((old) => old && { ...old, editable: d.editable });
      setRowStatus(id, { reloading: false, conflict: false, rebased: true, current: fresh.bag_count });
    } catch (e) {
      if (alive.current && scopeRef.current === mScope) setRowStatus(id, { reloading: false, error: `Could not reload: ${formatApiError(e)}. Your entry is kept.` });
    }
  }

  async function fetchFreshPrintRows() {
    const mine = ++printSeq.current;
    const mScope = scopeRef.current;
    const sig = privacySignature(getPrivacySession());
    const stale = () => !alive.current || mine !== printSeq.current || scopeRef.current !== mScope || privacySignature(getPrivacySession()) !== sig;
    const { data: response } = await api.get(`/sharing-tree/distribution?year=${year}`);
    if (stale()) return null;
    const d = checkedData(response, year);
    return savedRowsSorted(d.rows, nameHidden);
  }

  async function openPrint() {
    if (anyPending() || dirtyIds().length) { setWarning("Save or discard unsaved bag counts before printing."); return; }
    setWarning("");
    const mScope = scopeRef.current;
    const expectedSeq = printSeq.current + 1;
    setPrintState({ loading: true, error: "", rows: [] });
    try {
      const printable = await fetchFreshPrintRows();
      if (printable) setPrintState({ loading: false, error: "", rows: printable });
    } catch (e) {
      if (alive.current && scopeRef.current === mScope && printSeq.current === expectedSeq) setPrintState((p) => p && { loading: false, error: formatApiError(e), rows: [] });
    }
  }

  async function doPrint() {
    if (anyPending() || dirtyIds().length) { setPrintState((p) => p && { ...p, error: "Unsaved bag counts exist. Close this view and save first." }); return; }
    const sig = privacySignature(getPrivacySession());
    if (sig !== sigRef.current) { setPrintState(null); setWarning("Privacy settings changed. Reopen the print view."); return; }
    setPrintState((p) => p && { ...p, loading: true, error: "", rows: [] });
    try {
      const printable = await fetchFreshPrintRows();
      if (!printable) return;
      if (privacySignature(getPrivacySession()) !== sigRef.current) { setPrintState(null); return; }
      flushSync(() => setPrintState({ loading: false, error: "", rows: printable }));
      document.body.classList.add("fb-dist-print-ready");
      window.print();
      document.body.classList.remove("fb-dist-print-ready");
    } catch (e) {
      if (alive.current) setPrintState((p) => p && { ...p, loading: false, rows: [], error: formatApiError(e) });
    }
  }

  const filtered = useMemo(() => rows.filter((r) => matchesDistributionSearch(r, query, nameHidden)), [rows, query, nameHidden]);
  const savedCount = rows.filter((r) => r.bag_count !== null && r.bag_count !== undefined).length;
  const hiddenLabel = (field, v) => (hidden.includes(field) ? <span style={muted}>Hidden</span> : v || "—");

  return (
    <section className="fb-card p-4 sm:p-6" data-testid="distribution-checklist" aria-labelledby="distribution-checklist-title">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 id="distribution-checklist-title" className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Distribution Checklist</h3>
          <p className="text-sm" style={muted}>Record bags packed per household for {year}. {valid && `${savedCount} of ${rows.length} recorded.`}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
        <button type="button" data-testid="distribution-refresh" onClick={() => { if (!anyPending() && !dirtyIds().length) { setStatus({}); load(); } }}
                disabled={anyPending() || dirtyIds().length > 0} className="text-sm px-3 py-2 border rounded-lg disabled:opacity-50" style={border}>
          Refresh saved counts
        </button>
        <button type="button" data-testid="distribution-print-btn" onClick={openPrint} disabled={!valid || !!loadError}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border hover:bg-stone-50 disabled:opacity-50" style={border}>
          <Printer className="w-4 h-4" /> Print distribution checklist
        </button>
        </div>
      </div>
      <p className="text-xs mb-3" style={muted}>Household ID is the bag number. Enter the actual number of packed bags; a blank count means not entered. Printing includes every active household, with saved counts only.</p>
      {warning && <div role="alert" data-testid="distribution-warning" className="flex items-start gap-2 text-sm rounded-lg px-3 py-2 mb-3 fb-status-warning"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {warning}</div>}
      {valid && !editable && (
        <p className="text-xs mb-3" style={muted} data-testid="distribution-readonly-note">
          {!canWriteRole ? "Your role cannot edit bag counts." : "This program year is read-only."}
        </p>
      )}
      {loadError && !valid && (
        <div role="alert" className="text-sm" data-testid="distribution-error">
          Could not load the distribution checklist. {loadError}{" "}
          <button type="button" data-testid="distribution-retry" onClick={load} className="underline font-semibold">Retry</button>
        </div>
      )}
      {!valid && !loadError && <div role="status" className="py-6 text-sm text-center" style={muted} data-testid="distribution-loading">Loading distribution checklist…</div>}
      {valid && rows.length === 0 && <div className="py-8 text-center text-sm" style={muted}>No enrolled households for {year}.</div>}
      {valid && rows.length > 0 && (
        <>
          <div className="relative mb-3 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={muted} />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="distribution-search"
                   aria-label="Search distribution households" placeholder={nameHidden ? "Search household ID" : "Search name or household ID"}
                   className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm" style={border} />
          </div>
          {filtered.length === 0 && <div className="py-6 text-center text-sm" style={muted} data-testid="distribution-search-empty">No households match “{query.trim()}”.</div>}
          {filtered.length > 0 && (
            <ReportTableScroll label="Distribution Checklist table">
              <table className="w-full min-w-[860px] text-sm fb-dist-table">
                <thead><tr style={{ background: "var(--fb-bg)" }}>
                  {["Household / Bag ID", "Contact Name", "Address", "Phone", "Youth Gift Cards", "Food Voucher", "Bags", "Save"].map((h) => <th key={h} scope="col" className={TH} style={muted}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {filtered.map((r) => {
                    const id = r.household_record_id;
                    const st = status[id] || {};
                    const draft = drafts[id];
                    const text = draft === undefined ? baseText(r) : draft;
                    const changed = draft !== undefined && draft !== baseText(r);
                    const parsed = parseBagCount(text);
                    return (
                      <tr key={id} className={`border-t align-top ${changed ? "fb-dist-row-dirty" : ""}`} style={border} data-testid={`distribution-row-${r.household_id}`}>
                        <td className="font-mono text-xs">{r.household_id}</td>
                        <td className="font-medium">{nameHidden ? <span style={muted}>Name hidden</span> : (r.contact_name || "—")}</td>
                        <td style={{ overflowWrap: "anywhere" }}>{hiddenLabel("address", r.address)}</td>
                        <td>{hiddenLabel("phone", r.phone ? formatPhone(r.phone) : "")}</td>
                        <td className="tabular-nums">{r.gift_card_count ?? 0}</td>
                        <td className="tabular-nums">{tierText(r.tier_amount)}</td>
                        <td>
                          <input type="text" inputMode="numeric" className="fb-dist-input" value={text} disabled={!editable || st.saving}
                                 data-testid={`distribution-bags-${r.household_id}`} aria-label={`Bags for household ${r.household_id}`}
                                  placeholder="Not entered"
                                 aria-invalid={changed && !!parsed.error}
                                 onChange={(e) => edit(id, e.target.value)}
                                 onKeyDown={(e) => { if (e.key === "Enter") save(r); }} />
                          {editable && <button type="button" className="text-xs underline ml-1" disabled={st.saving || st.reloading}
                            onClick={() => edit(id, "")} aria-label={`Clear bag count for ${r.household_id}`}>Clear</button>}
                          {changed && <div className="text-xs mt-1" style={muted}>Saved: {baseText(r) || "Not entered"}</div>}
                        </td>
                        <td>
                          <div className="flex items-center gap-2 flex-wrap">
                            {editable && (
                              <button type="button" data-testid={`distribution-save-${r.household_id}`} onClick={() => save(r)}
                                      disabled={!changed || st.saving || st.conflict || st.reloading}
                                      className="fb-btn-primary px-3 py-1 rounded-lg text-xs font-semibold disabled:opacity-50">
                                {st.saving ? "Saving…" : "Save"}
                              </button>
                            )}
                            {st.saved && !changed && <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--fb-success, #166534)" }} role="status" data-testid={`distribution-saved-${r.household_id}`}><CheckCircle className="w-3.5 h-3.5" /> Saved</span>}
                            {changed && <button type="button" className="text-xs underline" disabled={st.saving || st.reloading}
                              onClick={() => { setDrafts((d) => { const next = { ...d }; delete next[id]; return next; }); setRowStatus(id, null); setWarning(""); }}>
                              Discard
                            </button>}
                          </div>
                          {changed && parsed.error && !st.error && <p className="text-xs mt-1" style={{ color: "var(--fb-error)" }} data-testid={`distribution-invalid-${r.household_id}`}>{parsed.error}</p>}
                          {st.error && <p role="alert" className="text-xs mt-1 font-medium" style={{ color: "var(--fb-error)" }} data-testid={`distribution-error-${r.household_id}`}>{st.error}</p>}
                          {st.conflict && (
                            <div role="alert" className="text-xs rounded-lg p-2 mt-1 fb-status-warning space-y-1" data-testid={`distribution-conflict-${r.household_id}`}>
                              <div>Someone else changed this count. Your entry is kept.</div>
                              <button type="button" data-testid={`distribution-reload-${r.household_id}`} disabled={st.reloading} onClick={() => reloadRow(r)}
                                      className="px-2 py-1 rounded border bg-white font-semibold disabled:opacity-50" style={border}>
                                {st.reloading ? "Reloading…" : "Reload current count"}
                              </button>
                            </div>
                          )}
                          {st.rebased && !st.conflict && (
                            <p className="text-xs mt-1 fb-status-warning rounded p-1.5" data-testid={`distribution-rebased-${r.household_id}`}>
                              Current saved count is {st.current ?? "blank"}. Press Save to replace it with your entry.
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ReportTableScroll>
          )}
        </>
      )}
      {printState && createPortal(
        <div className="fb-dist-overlay" role="dialog" aria-modal="true" aria-label="Pickup sheet" data-testid="distribution-print-modal">
          <div className="fb-dist-modal">
            <div className="flex items-center justify-between gap-2 mb-3 fb-dist-noprint">
              <h4 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>Pickup sheet {year}</h4>
              <div className="flex gap-2">
                <button type="button" data-testid="distribution-do-print" onClick={doPrint} disabled={printState.loading}
                        className="fb-btn-primary px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                  <Printer className="w-4 h-4" /> {printState.loading ? "Loading…" : "Print"}
                </button>
                <button type="button" data-testid="distribution-print-close" aria-label="Close" onClick={() => { printSeq.current += 1; setPrintState(null); }}
                        className="px-3 py-2 rounded-lg border" style={border}><X className="w-4 h-4" /></button>
              </div>
            </div>
            {printState.error && <p role="alert" className="text-sm mb-2 fb-dist-noprint" style={{ color: "var(--fb-error)" }} data-testid="distribution-print-error">{printState.error}</p>}
            {printState.loading && !printState.rows.length && <p role="status" className="text-sm py-6 text-center">Loading current saved counts…</p>}
            <p className="fb-dist-native-print-notice">Use the Print button in the distribution checklist to verify current saved data before printing.</p>
            {!printState.loading && !printState.error && printState.rows.length === 0 && <p className="text-sm py-6 text-center" data-testid="distribution-print-empty">No enrolled households for {year}.</p>}
            {printState.rows.length > 0 && (
              <table className="fb-dist-print-table" data-testid="distribution-print-table">
                <thead>
                  <tr><th colSpan={8} style={{ fontSize: 14 }}>Sharing Tree {year} — Distribution Checklist (household ID = bag number)</th></tr>
                  <tr>{["Household / Bag ID", "Contact Name", "Address", "Phone", "Youth Gift Cards", "Food Voucher", "Bags", "Picked up"].map((h) => <th key={h} scope="col">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {printState.rows.map((r) => (
                    <tr key={r.household_record_id} data-testid={`distribution-print-row-${r.household_id}`}>
                      <td>{r.household_id}</td>
                      <td>{nameHidden ? "Name hidden" : (r.contact_name || "")}</td>
                      <td>{hidden.includes("address") ? "Hidden" : (r.address || "")}</td>
                      <td>{hidden.includes("phone") ? "Hidden" : (r.phone ? formatPhone(r.phone) : "")}</td>
                      <td>{r.gift_card_count ?? 0}</td>
                      <td>{r.tier_amount === null || r.tier_amount === undefined || r.tier_amount === "" ? "" : `$${r.tier_amount}`}</td>
                      <td>{r.bag_count}</td>
                      <td><span className="fb-dist-box" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>, document.body)}
    </section>
  );
}
